import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { CURRENCIES, ACCESSORIAL_OPTIONS, currencySymbol } from "@/lib/constants";

const emptyAccessorial = { type: "Fuel Surcharge", description: "", amount: "" };

const initial = {
  carrier_id: "",
  carrier_name: "",
  mc_number: "",
  dot_number: "",
  carrier_contact_name: "",
  carrier_contact_phone: "",
  carrier_contact_email: "",
  driver_name: "",
  driver_phone: "",
  truck_number: "",
  trailer_number: "",
  agreed_rate: "",
  currency: "USD",
  payment_terms: "Net 30 days upon receipt of clean POD & signed BOL",
  accessorials: [],
  notes: "",
  save_carrier: true,
};

export default function RateConfDialog({ open, onOpenChange, jobId, rc, defaultRate, defaultCurrency, defaultAccessorials, onCreated, onUpdated }) {
  const isEdit = !!rc;
  const [form, setForm] = useState({
    ...initial,
    agreed_rate: defaultRate || "",
    currency: defaultCurrency || "USD",
    accessorials: Array.isArray(defaultAccessorials) ? defaultAccessorials.map((a) => ({ ...emptyAccessorial, ...a, amount: a.amount ?? "" })) : [],
  });
  const [busy, setBusy] = useState(false);
  const [carriers, setCarriers] = useState([]);

  useEffect(() => {
    if (open) {
      if (isEdit) {
        setForm({
          ...initial,
          carrier_id: rc.carrier_id || "",
          carrier_name: rc.carrier_name || "",
          mc_number: rc.mc_number || "",
          dot_number: rc.dot_number || "",
          carrier_contact_name: rc.carrier_contact_name || "",
          carrier_contact_phone: rc.carrier_contact_phone || "",
          carrier_contact_email: rc.carrier_contact_email || "",
          driver_name: rc.driver_name || "",
          driver_phone: rc.driver_phone || "",
          truck_number: rc.truck_number || "",
          trailer_number: rc.trailer_number || "",
          agreed_rate: rc.agreed_rate ?? "",
          currency: rc.currency || "USD",
          payment_terms: rc.payment_terms || initial.payment_terms,
          accessorials: Array.isArray(rc.accessorials)
            ? rc.accessorials.map((a) => ({ ...emptyAccessorial, ...a, amount: a.amount ?? "" }))
            : [],
          notes: rc.notes || "",
          save_carrier: false,
        });
      } else {
        setForm({
          ...initial,
          agreed_rate: defaultRate || "",
          currency: defaultCurrency || "USD",
          accessorials: Array.isArray(defaultAccessorials)
            ? defaultAccessorials.map((a) => ({ ...emptyAccessorial, ...a, amount: a.amount ?? "" }))
            : [],
        });
      }
      (async () => {
        try {
          const { data } = await api.get("/carriers");
          setCarriers(data);
        } catch { /* no-op */ }
      })();
    }
  }, [open, isEdit, rc, defaultRate, defaultCurrency, defaultAccessorials]);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const pickCarrier = (cid) => {
    const c = carriers.find((x) => x.id === cid);
    if (!c) return;
    setForm((f) => ({
      ...f,
      carrier_id: c.id,
      carrier_name: c.name || "",
      mc_number: c.mc_number || "",
      dot_number: c.dot_number || "",
      carrier_contact_name: c.contact_name || "",
      carrier_contact_phone: c.contact_phone || "",
      carrier_contact_email: c.contact_email || "",
      save_carrier: false, // don't re-save; user can toggle to update
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.carrier_name || !form.mc_number || !form.agreed_rate) {
      toast.error("Carrier name, MC# and rate are required");
      return;
    }
    setBusy(true);
    try {
      const accessorialRows = form.accessorials
        .filter((a) => a && a.type && (a.amount !== "" && Number(a.amount) !== 0))
        .map((a) => ({
          type: a.type,
          description: a.description || null,
          amount: Number(a.amount) || 0,
        }));
      const payload = {
        ...form,
        agreed_rate: Number(form.agreed_rate),
        accessorials: accessorialRows,
      };
      if (isEdit) {
        const { data } = await api.patch(`/rate-confirmations/${rc.id}`, payload);
        toast.success(`Rate confirmation ${data.rc_number} updated`);
        onUpdated?.(data);
      } else {
        const { data } = await api.post(`/jobs/${jobId}/rate-confirmation`, payload);
        toast.success(`Rate confirmation ${data.rc_number} created`);
        onCreated?.(data);
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sharp max-w-2xl max-h-[90vh] overflow-y-auto border-zinc-950">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold tracking-tight">
            {isEdit ? `Edit rate confirmation ${rc?.rc_number || ""}` : "New rate confirmation"}
          </DialogTitle>
          <p className="text-sm text-zinc-600">Carrier will be billed by Apex. Bill-to on the RC is always Apex Supply Chain Solutions.</p>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {carriers.length > 0 && (
            <div className="border border-zinc-200 bg-zinc-50 p-3">
              <label className="mono-label">Pick from saved carriers (only active — COI valid)</label>
              <Select value={form.carrier_id} onValueChange={pickCarrier}>
                <SelectTrigger className="sharp mt-1 bg-white" data-testid="rc-saved-carrier"><SelectValue placeholder="Select a saved carrier to pre-fill" /></SelectTrigger>
                <SelectContent>
                  {carriers.filter((c) => c.is_active).map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name} — MC {c.mc_number}{c.coi_days_to_expiry != null ? ` · COI ${c.coi_days_to_expiry}d` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {carriers.some((c) => !c.is_active) && (
                <p className="mono-label !text-amber-800 mt-2">
                  {carriers.filter((c) => !c.is_active).length} carrier(s) hidden — COI expired or missing. Upload a new COI on their profile to re-enable.
                </p>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="mono-label">Carrier name *</label>
              <Input required value={form.carrier_name} onChange={upd("carrier_name")} className="sharp mt-1 apex-input" data-testid="rc-carrier-name" />
            </div>
            <div>
              <label className="mono-label">MC # *</label>
              <Input required value={form.mc_number} onChange={upd("mc_number")} className="sharp mt-1 apex-input font-mono" data-testid="rc-mc" />
            </div>
            <div>
              <label className="mono-label">DOT #</label>
              <Input value={form.dot_number} onChange={upd("dot_number")} className="sharp mt-1 apex-input font-mono" />
            </div>
            <div>
              <label className="mono-label">Carrier contact</label>
              <Input value={form.carrier_contact_name} onChange={upd("carrier_contact_name")} className="sharp mt-1 apex-input" />
            </div>
            <div>
              <label className="mono-label">Contact phone</label>
              <Input value={form.carrier_contact_phone} onChange={upd("carrier_contact_phone")} className="sharp mt-1 apex-input" />
            </div>
            <div>
              <label className="mono-label">Contact email</label>
              <Input type="email" value={form.carrier_contact_email} onChange={upd("carrier_contact_email")} className="sharp mt-1 apex-input" />
            </div>
          </div>

          <div className="border-t border-zinc-200 pt-4">
            <div className="mono-label mb-3">DRIVER & EQUIPMENT</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mono-label">Driver name</label>
                <Input value={form.driver_name} onChange={upd("driver_name")} className="sharp mt-1 apex-input" data-testid="rc-driver" />
              </div>
              <div>
                <label className="mono-label">Driver phone</label>
                <Input value={form.driver_phone} onChange={upd("driver_phone")} className="sharp mt-1 apex-input" />
              </div>
              <div>
                <label className="mono-label">Truck #</label>
                <Input value={form.truck_number} onChange={upd("truck_number")} className="sharp mt-1 apex-input font-mono" />
              </div>
              <div>
                <label className="mono-label">Trailer #</label>
                <Input value={form.trailer_number} onChange={upd("trailer_number")} className="sharp mt-1 apex-input font-mono" />
              </div>
            </div>
          </div>

          <div className="border-t border-zinc-200 pt-4">
            <div className="mono-label mb-3">AGREEMENT</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mono-label">Agreed rate *</label>
                <Input required type="number" step="0.01" value={form.agreed_rate} onChange={upd("agreed_rate")}
                  className="sharp mt-1 apex-input font-mono" data-testid="rc-rate" />
              </div>
              <div>
                <label className="mono-label">Currency</label>
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger className="sharp mt-1 h-10" data-testid="rc-currency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <label className="mono-label">Payment terms</label>
                <Input value={form.payment_terms} onChange={upd("payment_terms")} className="sharp mt-1 apex-input" />
              </div>
              <div className="col-span-2">
                <label className="mono-label">Notes</label>
                <Textarea rows={3} value={form.notes} onChange={upd("notes")} className="sharp mt-1 apex-input" />
              </div>
            </div>
          </div>

          <div className="border border-zinc-950 bg-zinc-50 p-3" data-testid="rc-accessorials-section">
            <div className="flex items-center justify-between">
              <div className="mono-label !text-zinc-900">
                Accessorial Charges ({form.accessorials.length})
              </div>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setForm({ ...form, accessorials: [...form.accessorials, { ...emptyAccessorial }] })}
                className="sharp h-8 border-zinc-400 bg-white"
                data-testid="rc-add-accessorial"
              >
                <Plus className="h-3.5 w-3.5 mr-1" /> Add charge
              </Button>
            </div>
            {form.accessorials.length === 0 ? (
              <p className="mt-3 text-xs text-zinc-600">
                Add fuel surcharge, detention, lumper, etc. These will appear on the rate confirmation
                and in the carrier email.
              </p>
            ) : (
              <>
                <div className="mt-3 space-y-2">
                  {form.accessorials.map((a, idx) => {
                    const isOther = a.type === "Other";
                    return (
                      <div key={idx} className="grid grid-cols-12 gap-2 items-end bg-white border border-zinc-200 p-2" data-testid={`rc-accessorial-row-${idx}`}>
                        <div className="col-span-12 md:col-span-4">
                          <label className="mono-label !text-[9px]">Type</label>
                          <Select
                            value={a.type}
                            onValueChange={(v) => {
                              const next = [...form.accessorials];
                              next[idx] = { ...next[idx], type: v };
                              setForm({ ...form, accessorials: next });
                            }}
                          >
                            <SelectTrigger className="sharp mt-1 h-9 apex-input" data-testid={`rc-accessorial-type-${idx}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {ACCESSORIAL_OPTIONS.map((o) => (
                                <SelectItem key={o} value={o}>{o === "Other" ? "Other (Specify)" : o}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-9 md:col-span-5">
                          <label className="mono-label !text-[9px]">
                            {isOther ? "Description (required)" : "Notes (optional)"}
                          </label>
                          <Input
                            value={a.description || ""}
                            onChange={(e) => {
                              const next = [...form.accessorials];
                              next[idx] = { ...next[idx], description: e.target.value };
                              setForm({ ...form, accessorials: next });
                            }}
                            placeholder={isOther ? "e.g. Wash-out" : "e.g. 2 hours @ shipper"}
                            className="sharp mt-1 apex-input"
                            data-testid={`rc-accessorial-desc-${idx}`}
                          />
                        </div>
                        <div className="col-span-3 md:col-span-2">
                          <label className="mono-label !text-[9px]">
                            Amount ({currencySymbol(form.currency)})
                          </label>
                          <Input
                            type="number"
                            step="0.01"
                            value={a.amount}
                            onChange={(e) => {
                              const next = [...form.accessorials];
                              next[idx] = { ...next[idx], amount: e.target.value };
                              setForm({ ...form, accessorials: next });
                            }}
                            className="sharp mt-1 apex-input font-mono"
                            data-testid={`rc-accessorial-amount-${idx}`}
                          />
                        </div>
                        <div className="col-span-12 md:col-span-1 flex justify-end">
                          <button
                            type="button"
                            onClick={() => setForm({ ...form, accessorials: form.accessorials.filter((_, i) => i !== idx) })}
                            className="h-9 w-9 flex items-center justify-center border border-zinc-300 text-rose-700 hover:bg-rose-50"
                            title="Remove charge"
                            data-testid={`rc-remove-accessorial-${idx}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-3 flex items-center justify-end gap-4 text-sm border-t border-zinc-200 pt-2">
                  <span className="mono-label">Accessorials subtotal</span>
                  <span className="font-mono font-semibold" data-testid="rc-accessorials-subtotal">
                    {currencySymbol(form.currency)}
                    {form.accessorials.reduce((s, a) => s + (Number(a.amount) || 0), 0).toLocaleString()}
                  </span>
                  <span className="mono-label">Total payable</span>
                  <span className="font-mono font-bold text-[#0056B3]" data-testid="rc-total-payable">
                    {currencySymbol(form.currency)}
                    {((Number(form.agreed_rate) || 0) + form.accessorials.reduce((s, a) => s + (Number(a.amount) || 0), 0)).toLocaleString()}
                  </span>
                </div>
              </>
            )}
          </div>

          <div className="flex items-center gap-2 border-t border-zinc-200 pt-4">
            <Checkbox
              id="save-carrier"
              checked={form.save_carrier}
              onCheckedChange={(v) => setForm({ ...form, save_carrier: !!v })}
              data-testid="rc-save-carrier" />
            <label htmlFor="save-carrier" className="text-sm cursor-pointer">
              {form.carrier_id ? "Update saved carrier record" : "Save this carrier for future rate confirmations"}
            </label>
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" className="sharp border-zinc-300" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy} className="sharp bg-[#0056B3] hover:bg-[#004494]" data-testid="rc-submit">
              {busy ? (isEdit ? "Saving..." : "Creating...") : (isEdit ? "Save changes" : "Create rate confirmation")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
